// Per-strategy PRESENTATION CONTRACT for the canonical risk score.
//
// WHAT KIND OF MODELS ARE THESE? These strategies are rule-based, NOT trained
// ML models. adaptiveBalanced/balanced/adaptive are weighted-linear
// "susceptibility" scores (weighted sum of the `features` below); conservative
// is a binary 2-of-5 vote (count of confirmed sell conditions, no weights).
// The weights were tuned by Optuna/DEAP optimization on SageMaker — the output
// is these constants, not a model file. Authoritative backend source:
// funviz-amplify/resources/market-health-compute/index.mjs (`*_WEIGHTS`,
// `STRATEGY_MODES`); full methodology in that repo's whitepaper §13.
//
// The canonical score is the always-computed weighted susceptibility, shown
// UNCHANGED across the Risk Pressure bar, ModelState, Heartbeat sparklines and
// Score History. Higher = more risk. The score is context only — the authoritative
// signal is always the Invested/Cash state, never the score crossing a
// boundary.
//
// Each config exposes:
//   - scoreField(s): the persisted canonical field(s) to prefer, per mode.
//   - scoreDomain / scoreTicks: the bar/axis domain and tick positions.
//   - riskBoundary / riskBoundaryLabel: where the risk band begins (green
//     below, red at/above) and the human-readable label.
//   - features / featuresField: kept for DimensionBars' factor breakdown and
//     as the client-side fallback source for getStrategyScore.
//
// getStrategyScore(entry, mode) resolves the canonical score per entry with a
// 3-step priority so the frontend works whether or not the backend has
// deployed the persisted fields yet.

export const STRATEGY_CONFIGS = {
  adaptiveBalanced: {
    scoreField: 'adaptiveBalancedRiskScore',
    scoreDomain: [0, 100],
    scoreTicks: [0, 25, 50, 75, 100],
    riskBoundary: 54.325,
    riskBoundaryLabel: 'Risk threshold 54.3',
    featuresField: 'adaptiveBalancedFeatures',
    features: [
      { key: 'ycDays', label: 'Monetary Stress · Recency', weight: 0.357 },
      { key: 'ycSpread', label: 'Monetary Stress · Level', weight: 0.491 },
      { key: 'rally', label: 'Market Extension', weight: 0.050 },
      { key: 'breadthTrend', label: 'Participation Trend', weight: 0.481 },
      { key: 'breadthLevel', label: 'Participation Level', weight: 0.278 },
      { key: 'credit', label: 'Credit Conditions', weight: 0.222 },
      { key: 'dd', label: 'Price Decline Depth', weight: 0.103 },
    ],
  },
  balanced: {
    scoreField: 'balancedRiskScore',
    scoreDomain: [0, 100],
    scoreTicks: [0, 25, 50, 75, 100],
    riskBoundary: 56.72,
    riskBoundaryLabel: 'Risk threshold 56.7',
    featuresField: 'balancedFeatures',
    features: [
      { key: 'ycDays', label: 'Monetary Stress · Recency', weight: 0.169 },
      { key: 'ycSpread', label: 'Monetary Stress · Level', weight: 0.259 },
      { key: 'rally', label: 'Market Extension', weight: 0.052 },
      { key: 'breadthTrend', label: 'Participation Trend', weight: 0.264 },
      { key: 'breadthLevel', label: 'Participation Level', weight: 0.069 },
      { key: 'credit', label: 'Credit Conditions', weight: 0.121 },
      { key: 'dd', label: 'Price Decline Depth', weight: 0.280 },
      { key: 'vix', label: 'Volatility Regime', weight: 0.057 },
    ],
  },
  adaptive: {
    scoreField: 'adaptiveRiskScore',
    scoreDomain: [0, 100],
    scoreTicks: [0, 25, 50, 75, 100],
    riskBoundary: 56.8,
    riskBoundaryLabel: 'Risk threshold 56.8',
    featuresField: 'adaptiveFeatures',
    features: [
      { key: 'ycDays', label: 'Monetary Stress · Recency', weight: 0.862 },
      { key: 'ycSpread', label: 'Monetary Stress · Level', weight: 0.055 },
      { key: 'rally', label: 'Market Extension', weight: 0.255 },
      { key: 'credit', label: 'Credit Conditions', weight: 0.243 },
      { key: 'dd', label: 'Price Decline Depth', weight: 0.479 },
    ],
  },
  conservative: {
    // Conservative has no weighted-susceptibility model: its canonical score
    // is the count of confirmed sell conditions (0–5). There is no features
    // fallback — the count is always persisted directly.
    scoreField: 'conservativeConfirms',
    scoreDomain: [0, 5],
    scoreTicks: [0, 1, 2, 3, 4, 5],
    riskBoundary: 2,
    // Conservative is the one strategy whose boundary IS the sell condition:
    // when the confirmed-signal count reaches 2, the strategy's sell rule is
    // met (subject to its price gate + lag). So it's labeled a "Sell trigger"
    // rather than the softer "Risk threshold" the susceptibility strategies
    // use, where crossing the line is proximity, not the trade itself.
    riskBoundaryLabel: 'Sell trigger',
    featuresField: null,
    features: null,
  },
};

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

// Compute weighted susceptibility from stored *Features using the strategy's
// per-strategy weights. Same math the backend uses. Returns null when there
// are no features to weight (so the caller can fall through to "unavailable"
// rather than fabricating a 0).
export function computeSusceptibility(featuresObj, config) {
  if (!featuresObj || !config || !config.features) return null;
  let weightedSum = 0;
  let activeWeight = 0;
  for (const { key, weight } of config.features) {
    const v = featuresObj[key];
    if (isFiniteNumber(v)) {
      weightedSum += weight * Math.max(0, Math.min(100, v));
      activeWeight += weight;
    }
  }
  return activeWeight > 0 ? weightedSum / activeWeight : null;
}

/**
 * Resolve the canonical risk score for an entry in the given mode.
 *
 * Priority:
 *   1. A persisted canonical field, if present and finite (exact passthrough —
 *      0 and decimals preserved, never inverted/clamped/rounded).
 *   2. Else compute weighted susceptibility client-side from the stored
 *      *Features (non-conservative modes only).
 *   3. Else null (render as unavailable / gap; never fabricate 0).
 *
 * Higher = more risk in every mode.
 */
export function getStrategyScore(entry, mode) {
  const config = STRATEGY_CONFIGS[mode];
  if (!entry || !config) return null;

  // 1. Persisted canonical field — exact finite passthrough (incl. 0/decimals).
  const persisted = entry[config.scoreField];
  if (isFiniteNumber(persisted)) return persisted;

  // 2. Client-side weighted susceptibility from features (non-conservative).
  if (config.featuresField) {
    const computed = computeSusceptibility(entry[config.featuresField], config);
    if (isFiniteNumber(computed)) return computed;
  }

  // 3. Unavailable.
  return null;
}
